import { onBeforeUnmount, onMounted } from 'vue';
import { useSettingsStore } from '@/stores/settings.store';
import { useToast } from '@/composables/useToast';

/**
 * Double-tap the page background to flip light/dark.
 *
 * A shortcut, never the only way: the visible sun/moon button in SubPageHeader
 * remains, because a hidden gesture helps only the person who already knows it
 * exists, and it has no keyboard or screen-reader equivalent.
 *
 * Two rules keep it from firing by accident, which is the real hazard — a theme
 * that flips for no visible reason is worse than one that is hard to reach:
 *
 *  1. INTERACTIVE TARGETS ARE IGNORED. Buttons, links, inputs, `contenteditable`
 *     (double-tap selects a word in the note composer), cards and list rows all
 *     opt out, so the gesture only fires on genuinely empty space.
 *  2. IT MUST BE ONE GESTURE. Both taps within {@link TAP_WINDOW_MS} and within
 *     {@link TAP_SLOP_PX} of each other — two deliberate taps in one spot, not
 *     two unrelated taps that happen to be close in time.
 *
 * A toast names the result, so a surprise flip is at least self-explanatory.
 */

/** Max gap between the two taps. Matches the usual double-click threshold. */
const TAP_WINDOW_MS = 320;

/** Max distance between them; beyond this it's two separate taps. */
const TAP_SLOP_PX = 28;

/**
 * Anything that owns its own tap behaviour.
 *
 * `[data-no-theme-tap]` is the opt-out for a whole surface: the file viewer
 * sets it on its root because its images double-tap to ZOOM (Swiper's zoom
 * module), and Swiper does not stop `pointerup` propagation — without the
 * opt-out, every zoom would also flip the theme and pop the toast.
 */
const INTERACTIVE = [
  'button',
  'a',
  'input',
  'textarea',
  'select',
  'label',
  'ion-icon',
  '[contenteditable]',
  '[role="button"]',
  '[role="tab"]',
  '[role="listitem"]',
  '[data-no-theme-tap]',
].join(',');

export function useDoubleTapTheme(): void {
  const settings = useSettingsStore();
  const toast = useToast();

  let lastAt = 0;
  let lastX = 0;
  let lastY = 0;

  async function announce(): Promise<void> {
    // Neutral, and deliberately brief: this only confirms a gesture the user
    // just made, so it should not read as a success or an alert.
    await toast.show(settings.isDark ? 'Dark mode' : 'Light mode', { duration: 1200 });
  }

  function onPointerUp(event: PointerEvent): void {
    // Mouse right/middle buttons and stylus barrel taps aren't this gesture.
    if (event.button !== 0) return;

    const target = event.target as Element | null;
    if (target?.closest?.(INTERACTIVE)) {
      // Tapping a control resets the sequence, so a tap on a card followed by a
      // tap on the background can't complete a "double tap".
      lastAt = 0;
      return;
    }

    const now = event.timeStamp;
    const withinTime = now - lastAt <= TAP_WINDOW_MS;
    const withinSlop =
      Math.abs(event.clientX - lastX) <= TAP_SLOP_PX &&
      Math.abs(event.clientY - lastY) <= TAP_SLOP_PX;

    if (lastAt && withinTime && withinSlop) {
      settings.setThemeMode(settings.isDark ? 'light' : 'dark');
      void announce();
      lastAt = 0; // A triple tap must not toggle twice.
      return;
    }

    lastAt = now;
    lastX = event.clientX;
    lastY = event.clientY;
  }

  onMounted(() => document.addEventListener('pointerup', onPointerUp));
  onBeforeUnmount(() => document.removeEventListener('pointerup', onPointerUp));
}
